/**
 * One tree replacement: customer, "1 Dwarf Jade", the note, the pickup status and
 * the pickup action, as columns or stacked depending on the list's width. For people
 * who may manage replacements, clicking the row opens the edit form.
 */
import { useState } from 'react';

import type { WorkshopTreeReplacement } from '../../../../api/workshops';

import { displayedNote, pickedUpLine, replacementName } from './tree-replacement-form';
import {
  TREE_REPLACEMENT_GRID,
  TREE_REPLACEMENT_MUTED,
  type TreeReplacementLayout,
} from './tree-replacement-styles';
import { TreeReplacementModal } from './TreeReplacementModal';
import { TreeReplacementName } from './TreeReplacementName';
import { TreeReplacementPickupToggle } from './TreeReplacementPickupToggle';
import { TreeReplacementTrees } from './TreeReplacementTrees';

export function TreeReplacementRow({
  pickup,
  workshopId,
  canManage,
  venueTimeZone,
  layout,
}: {
  pickup: WorkshopTreeReplacement;
  workshopId: string;
  canManage: boolean;
  venueTimeZone: string | null;
  layout: TreeReplacementLayout;
}) {
  const [editing, setEditing] = useState(false);
  const name = replacementName(pickup);
  const note = displayedNote(pickup);
  const status = pickup.pickedUp ? `Picked up ${pickedUpLine(pickup, venueTimeZone)}`.trim() : '';
  return (
    <>
      <div
        data-testid="tree-replacement-row"
        onClick={canManage ? () => setEditing(true) : undefined}
        style={{
          ...TREE_REPLACEMENT_GRID[layout],
          padding: '9px 6px',
          borderTop: '1px solid var(--nb-border-2)',
          cursor: canManage ? 'pointer' : undefined,
        }}
      >
        <TreeReplacementName name={name} editable={canManage} />
        <TreeReplacementTrees pickup={pickup} />
        <div style={{ ...TREE_REPLACEMENT_MUTED, gridArea: 'note' }}>{note}</div>
        <div style={{ ...TREE_REPLACEMENT_MUTED, gridArea: 'status' }}>{status}</div>
        <div
          style={{ gridArea: 'action', justifySelf: 'end' }}
          onClick={(e) => e.stopPropagation()}
        >
          {canManage && (
            <TreeReplacementPickupToggle
              workshopId={workshopId}
              treeReplacementId={pickup.id}
              customerName={name}
              pickedUp={pickup.pickedUp}
            />
          )}
        </div>
      </div>
      {editing && (
        <TreeReplacementModal
          workshopId={workshopId}
          view={{ mode: 'edit', treeReplacementId: pickup.id, customerName: name }}
          onClose={() => setEditing(false)}
        />
      )}
    </>
  );
}
